'use client';

import type { ComponentType, ReactElement } from 'react';
import { captureException } from '@sentry/core';
import { ErrorBoundary } from './ErrorBoundary';

interface WithErrorBoundaryOptions {
  fallback?: ReactElement<{ resetBoundary: () => void; error: Error | null }>;
  onError?: (error: Error) => void;
}

const EmptyFallback = () => null;
const defaultFallback = (<EmptyFallback />) as ReactElement<{ resetBoundary: () => void; error: Error | null }>;

export const withErrorBoundary = <TProps extends object>(
  Component: ComponentType<TProps>,
  options: WithErrorBoundaryOptions = {},
) => {
  const {
    fallback = defaultFallback,
    onError = (error) => captureException(error),
  } = options;

  const WrappedComponent = (props: TProps) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};
